// src/components/compliance/cameraCrop.ts
/**
 * Geometry of the KYC camera capture (Issue #1102).
 *
 * Pure arithmetic, no DOM: the live view shows the camera picture with
 * `object-cover`, so what the customer sees is only the middle cut-out of the
 * stream. The stored capture has to be exactly that cut-out — and for a
 * document only the ID-1 frame the customer aligned the card in.
 *
 * The overlay and the crop therefore come from the very same functions; a
 * second calculation in the component would sooner or later drift apart from
 * what is on screen.
 */
import {
  KYC_CAPTURE_MIN_LONG_EDGE_PX,
  KYC_DOCUMENT_CROP_MARGIN,
  KYC_DOCUMENT_FRAME_FILL,
  KYC_ID1_ASPECT_RATIO,
} from '@/constants/kyc'

/** What is being captured — a document gets the ID-1 frame, a selfie does not. */
export type CameraCaptureKind = 'document' | 'selfie'

/** Rectangle in camera pixels (source) or CSS pixels of the box (frame). */
export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

/** The document frame, additionally in percent of the box for the overlay. */
export interface FrameRect extends CropRect {
  leftPercent: number
  topPercent: number
  widthPercent: number
  heightPercent: number
}

function isPositive(value: number): boolean {
  return Number.isFinite(value) && value > 0
}

/**
 * The part of the camera picture that `object-cover` shows in a box of
 * `boxWidth × boxHeight` CSS pixels — in camera pixels, centred.
 *
 * `null` while the stream or the box has no dimensions yet (jsdom, first
 * frame); the caller then falls back to the full picture.
 */
export function visibleSourceRect(
  videoWidth: number,
  videoHeight: number,
  boxWidth: number,
  boxHeight: number,
): CropRect | null {
  if (![videoWidth, videoHeight, boxWidth, boxHeight].every(isPositive)) return null

  const scale = Math.max(boxWidth / videoWidth, boxHeight / videoHeight)
  const width = Math.min(videoWidth, Math.round(boxWidth / scale))
  const height = Math.min(videoHeight, Math.round(boxHeight / scale))

  return {
    x: Math.round((videoWidth - width) / 2),
    y: Math.round((videoHeight - height) / 2),
    width,
    height,
  }
}

/**
 * The ID-1 frame inside the box: as large as possible, at most
 * {@link KYC_DOCUMENT_FRAME_FILL} of the width *and* of the height, centred.
 */
export function documentFrameRect(boxWidth: number, boxHeight: number): FrameRect | null {
  if (![boxWidth, boxHeight].every(isPositive)) return null

  const width = Math.min(
    boxWidth * KYC_DOCUMENT_FRAME_FILL,
    boxHeight * KYC_DOCUMENT_FRAME_FILL * KYC_ID1_ASPECT_RATIO,
  )
  const height = width / KYC_ID1_ASPECT_RATIO
  const x = (boxWidth - width) / 2
  const y = (boxHeight - height) / 2

  return {
    x,
    y,
    width,
    height,
    leftPercent: (x / boxWidth) * 100,
    topPercent: (y / boxHeight) * 100,
    widthPercent: (width / boxWidth) * 100,
    heightPercent: (height / boxHeight) * 100,
  }
}

/**
 * The rectangle of the camera picture that is stored, in camera pixels.
 *
 * Selfie: the visible cut-out. Document: the frame plus a small margin per
 * side, never reaching outside the visible cut-out. `null` without dimensions.
 */
export function captureCropRect(
  videoWidth: number,
  videoHeight: number,
  boxWidth: number,
  boxHeight: number,
  kind: CameraCaptureKind,
): CropRect | null {
  const visible = visibleSourceRect(videoWidth, videoHeight, boxWidth, boxHeight)
  if (!visible) return null
  if (kind === 'selfie') return visible

  const frame = documentFrameRect(boxWidth, boxHeight)
  if (!frame) return visible

  const marginX = frame.width * KYC_DOCUMENT_CROP_MARGIN
  const marginY = frame.height * KYC_DOCUMENT_CROP_MARGIN
  // CSS pixels of the box → camera pixels of the visible cut-out
  const scaleX = visible.width / boxWidth
  const scaleY = visible.height / boxHeight

  const left = Math.max(visible.x, Math.round(visible.x + (frame.x - marginX) * scaleX))
  const top = Math.max(visible.y, Math.round(visible.y + (frame.y - marginY) * scaleY))
  const right = Math.min(
    visible.x + visible.width,
    Math.round(visible.x + (frame.x + frame.width + marginX) * scaleX),
  )
  const bottom = Math.min(
    visible.y + visible.height,
    Math.round(visible.y + (frame.y + frame.height + marginY) * scaleY),
  )

  return {
    x: left,
    y: top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  }
}

/** Whether a capture is large enough to be read comfortably by the reviewer. */
export function meetsMinResolution(width: number, height: number): boolean {
  return Math.max(width, height) >= KYC_CAPTURE_MIN_LONG_EDGE_PX
}
